import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { transferAlertsAPI } from '../services/api';
import { fmtDateTime, fmtTime } from '../utils/time';
import { FiAlertTriangle, FiRefreshCw, FiUser, FiArrowDown, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import '../styles/Alerts.css';

const SEV = {
    high:   { label: 'NGUY HIỂM CAO',   cls: 'sev-high',   icon: '🔴' },
    medium: { label: 'NGHI VẤN TRUNG BÌNH', cls: 'sev-medium', icon: '🟠' },
    low:    { label: 'CẢNH BÁO NHẸ',    cls: 'sev-low',    icon: '🟡' },
};

const $ = (n) => n == null ? '—' : '$' + Math.round(n).toLocaleString('vi-VN');
const $signed = (n) => {
    if (n == null) return '—';
    const r = Math.round(n);
    return (r >= 0 ? '+' : '') + '$' + r.toLocaleString('vi-VN');
};

const AlertCard = ({ a }) => {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const sev = SEV[a.severity];
    const r = a.balanceReconcile;
    const att = a.attacker;
    const vic = a.victim;
    const cur = a.currency;
    const isCash = cur === 'cash';

    // Gap = snapshot - log dự đoán. Positive = snapshot CÓ NHIỀU hơn log → khả năng rollback.
    const gap = r ? (isCash ? r.cashGap : r.bankGap) : null;
    const rollbackLikely = gap != null && gap >= a.amountPerTx;

    return (
        <div className={`alert-card ${sev.cls}`}>
            {/* HEADER */}
            <div className="alert-head">
                <div className="alert-head-left">
                    <span className="alert-sev-badge">{sev.icon} {sev.label}</span>
                    <span className="alert-amount">{$(a.totalAmount)}</span>
                    <span className="alert-cur-pill">{cur === 'cash' ? '💵 Cash' : cur === 'bank' ? '🏦 Bank' : cur}</span>
                </div>
                <div className="alert-head-right">
                    <div className="alert-time-row">
                        <strong>{fmtDateTime(a.burstStart)}</strong>
                        <span className="alert-span">trong {a.spanSec}s</span>
                    </div>
                </div>
            </div>

            {/* TÓM TẮT 1 DÒNG */}
            <div className="alert-summary">
                <strong>{att.playerName}</strong> đã chuyển <strong>{a.count} lần × {$(a.amountPerTx)} = {$(a.totalAmount)}</strong> sang <strong>{vic.playerName}</strong> trong {a.spanSec === 0 ? 'cùng giây' : a.spanSec + ' giây'}.
            </div>

            {/* CỜ BẰNG CHỨNG */}
            <div className="alert-flags">
                {a.balanceRollbackEvidence && (
                    <span className="flag-evidence flag-critical">
                        💰 BẰNG CHỨNG ROLLBACK · Số dư hiện tại nhiều hơn log dự đoán {$(gap)}
                    </span>
                )}
                {a.relogDetected && (
                    <span className="flag-evidence flag-warn">
                        🔄 ATTACKER ĐÃ RELOG trong 10 phút sau burst
                    </span>
                )}
                {a.mismatch !== 0 && (
                    <span className="flag-evidence flag-warn">
                        ⚠ MISMATCH: receiver {a.receivesLogged} event vs sender {a.sendsLogged} event
                    </span>
                )}
                {!a.balanceRollbackEvidence && !a.relogDetected && a.mismatch === 0 && (
                    <span className="flag-evidence flag-info">
                        ℹ Chỉ phát hiện burst, chưa có bằng chứng cứng (có thể chỉ là tip bạn bè)
                    </span>
                )}
            </div>

            {/* 2 KHUNG PLAYER */}
            <div className="alert-players-grid">
                {/* ATTACKER */}
                <div className="player-box attacker-box">
                    <div className="player-box-title">
                        <FiUser /> KẺ TẤN CÔNG (A)
                    </div>
                    <button
                        className="player-name-link"
                        onClick={() => att.citizenId && navigate(`/fivem-player/${att.citizenId}`)}
                    >
                        {att.playerName || '?'}
                    </button>
                    <div className="player-ids">
                        <code>CID: {att.citizenId}</code>
                        <code>ID: {att.serverId}</code>
                        <code>PID: {att.pid}</code>
                    </div>

                    <div className="balance-block">
                        <div className="balance-line">
                            <span>Số dư {cur} <em>trước khi chuyển</em>:</span>
                            <strong className="bal-neutral">{$(att.balanceBeforeBurst)}</strong>
                        </div>
                        <div className="balance-line">
                            <span>Đã chuyển đi (theo log):</span>
                            <strong className="bal-out">−{$(att.totalSentLogged)}</strong>
                        </div>
                        <div className="balance-line balance-divider">
                            <span>Theo log lẽ ra còn:</span>
                            <strong className="bal-neutral">{$(att.balanceAfterBurstLogged)}</strong>
                        </div>
                        {/* BANK trước burst */}
                        {att.bankBefore && (
                            <div className="balance-line balance-highlight">
                                <span>🏦 Bank <em>trước burst</em> ({att.bankBefore.minutesBeforeBurst}ph trước):</span>
                                <strong className="bal-neutral">{$(att.bankBefore.balance)}</strong>
                            </div>
                        )}
                        {/* BANK sau burst — đây là phép đo CHÍNH XÁC */}
                        {att.bankAfter ? (
                            <div className="balance-line balance-highlight">
                                <span>🏦 Bank <em>sau burst</em> ({att.bankAfter.minutesAfterBurst}ph sau · {att.bankAfter.isAddEvent ? '+tiền' : att.bankAfter.transactionType}):</span>
                                <strong className={a.bankRollback ? 'bal-rollback' : 'bal-neutral'}>{$(att.bankAfter.balance)}</strong>
                            </div>
                        ) : (
                            <div className="balance-line balance-meta">
                                <span>🏦 Bank sau burst:</span>
                                <span className="bal-meta-val">không có event nào trong 30 phút</span>
                            </div>
                        )}
                        {a.bankRollback && (
                            <div className="balance-line balance-result">
                                <span>🚨 BANK ĐÃ ĐƯỢC HỒI:</span>
                                <strong className="bal-rollback">{$signed(a.bankRollback.diff)}</strong>
                            </div>
                        )}
                    </div>
                </div>

                {/* ARROW */}
                <div className="alert-arrow-mid"><FiArrowDown /></div>

                {/* VICTIM */}
                <div className="player-box victim-box">
                    <div className="player-box-title">
                        <FiUser /> NẠN NHÂN / NGƯỜI NHẬN (B)
                    </div>
                    <button
                        className="player-name-link"
                        onClick={() => vic.citizenId && navigate(`/fivem-player/${vic.citizenId}`)}
                    >
                        {vic.playerName || '?'}
                    </button>
                    <div className="player-ids">
                        <code>CID: {vic.citizenId}</code>
                        <code>ID: {vic.serverId}</code>
                        <code>PID: {vic.pid}</code>
                    </div>

                    <div className="balance-block">
                        <div className="balance-line">
                            <span>Đã nhận được (theo log):</span>
                            <strong className="bal-in">+{$(vic.totalReceivedLogged)}</strong>
                        </div>
                        {vic.bankBefore && (
                            <div className="balance-line balance-divider">
                                <span>🏦 Bank <em>trước burst</em> ({vic.bankBefore.minutesBeforeBurst}ph trước):</span>
                                <strong className="bal-neutral">{$(vic.bankBefore.balance)}</strong>
                            </div>
                        )}
                        {vic.bankAfter ? (
                            <div className="balance-line balance-highlight">
                                <span>🏦 Bank <em>sau burst</em> ({vic.bankAfter.minutesAfterBurst}ph sau):</span>
                                <strong className="bal-in">{$(vic.bankAfter.balance)}</strong>
                            </div>
                        ) : (
                            <div className="balance-line balance-meta">
                                <span>🏦 Bank sau burst:</span>
                                <span className="bal-meta-val">không có event nào trong 30 phút</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* EXPAND DIỄN BIẾN */}
            <button className="alert-expand-btn" onClick={() => setExpanded(v => !v)}>
                {expanded ? <FiChevronUp /> : <FiChevronDown />}
                {expanded ? 'Thu gọn' : `Xem chi tiết ${a.count} lần chuyển`}
            </button>

            {expanded && (
                <div className="alert-detail">
                    <table className="timeline-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Thời gian</th>
                                <th>Số tiền chuyển</th>
                                <th>Số dư của A sau lần chuyển</th>
                                <th>Cách lần trước</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(a.senderTimeline || []).map((ev, i, arr) => {
                                const prev = i > 0 ? arr[i-1] : null;
                                const gap = prev ? Math.round((new Date(ev.time) - new Date(prev.time)) / 1000) : null;
                                return (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td className="ts-cell">{fmtTime(ev.time)}</td>
                                        <td className="bal-out">−{$(ev.amount)}</td>
                                        <td>{$(ev.balanceAfter)}</td>
                                        <td>{gap == null ? '—' : `+${gap}s`}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {r && (
                        <div className="reconcile-explain">
                            <h4>Cách suy luận rollback:</h4>
                            <ol>
                                <li>Sau lần chuyển cuối, log nói số {isCash ? 'cash' : 'bank'} của A còn <strong>{$(att.balanceAfterBurstLogged)}</strong>.</li>
                                <li>Snapshot hiện tại của A: <strong>{$(isCash ? r.snapshotCash : r.snapshotBank)}</strong> (cập nhật {fmtTime(r.snapshotUpdatedAt)}).</li>
                                <li>Chênh lệch: <strong className={gap > 0 ? 'bal-rollback' : ''}>{$signed(gap)}</strong>.</li>
                                {rollbackLikely
                                    ? <li>🚨 Snapshot có nhiều hơn log dự đoán <strong>{$(gap)}</strong> (≥ ít nhất 1 lần chuyển = {$(a.amountPerTx)}) → khả năng cao đã rollback.</li>
                                    : <li>Chênh lệch âm (snapshot ít hơn log) là bình thường — A đã chi tiêu thêm sau burst.</li>
                                }
                                <li className="muted">Lưu ý: cũng có khả năng A có thu nhập legit khác giữa burst và snapshot. Cần verify cross-check với log thu nhập của A.</li>
                            </ol>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Alerts = () => {
    const [alerts, setAlerts]   = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter]   = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [a, s] = await Promise.all([
                transferAlertsAPI.list({ severity: filter || undefined, limit: 300 }),
                transferAlertsAPI.summary(),
            ]);
            setAlerts(a.data.alerts || []);
            setSummary(s.data);
        } catch (e) {
            console.error(e);
            toast.error('Không tải được alerts');
        } finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    return (
        <div className="alerts-page">
            <div className="alerts-header">
                <div>
                    <h1 className="alerts-title">
                        <FiAlertTriangle className="alerts-icon" />
                        Cảnh Báo Exploit · Chuyển Tiền Trùng
                    </h1>
                    <p className="alerts-sub">
                        Phát hiện hành vi gửi tiền player-to-player nhiều lần liên tiếp cùng số tiền,
                        có thể là exploit tắt mạng + relog để được hồi tiền.
                    </p>
                </div>
                <button className="alerts-refresh" onClick={fetchAll} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spinning' : ''} /> Quét lại
                </button>
            </div>

            {summary && (
                <div className="alerts-summary">
                    <button className={`alerts-stat ${filter==='' ? 'active' : ''}`} onClick={() => setFilter('')}>
                        <span className="stat-num">{summary.total}</span>
                        <span className="stat-label">Tổng số nghi vấn</span>
                    </button>
                    <button className={`alerts-stat sev-high ${filter==='high' ? 'active' : ''}`} onClick={() => setFilter('high')}>
                        <span className="stat-num">{summary.severity.high}</span>
                        <span className="stat-label">🔴 Nguy hiểm cao</span>
                        <span className="stat-sub">{$(summary.totalAmountBySeverity.high)}</span>
                    </button>
                    <button className={`alerts-stat sev-medium ${filter==='medium' ? 'active' : ''}`} onClick={() => setFilter('medium')}>
                        <span className="stat-num">{summary.severity.medium}</span>
                        <span className="stat-label">🟠 Trung bình</span>
                        <span className="stat-sub">{$(summary.totalAmountBySeverity.medium)}</span>
                    </button>
                    <button className={`alerts-stat sev-low ${filter==='low' ? 'active' : ''}`} onClick={() => setFilter('low')}>
                        <span className="stat-num">{summary.severity.low}</span>
                        <span className="stat-label">🟡 Nhẹ</span>
                    </button>
                    <div className="alerts-stat alerts-stat-info">
                        <span className="stat-num">{summary.balanceRollbackCount}</span>
                        <span className="stat-label">💰 Có rollback xác nhận</span>
                    </div>
                    <div className="alerts-stat alerts-stat-info">
                        <span className="stat-num">{summary.relogCount}</span>
                        <span className="stat-label">🔄 Có relog sau burst</span>
                    </div>
                    <div className="alerts-stat alerts-stat-meta">
                        <span className="stat-label">Lần scan cuối</span>
                        <span className="stat-sub">{summary.lastScanAt ? fmtTime(summary.lastScanAt) : '—'}</span>
                    </div>
                </div>
            )}

            {loading && alerts.length === 0 ? (
                <div className="alerts-loading"><div className="loading-spinner" /></div>
            ) : alerts.length === 0 ? (
                <div className="alerts-empty">✅ Không có alert nào ở filter này</div>
            ) : (
                <div className="alerts-list">
                    {alerts.map((a, i) => <AlertCard key={i} a={a} />)}
                </div>
            )}
        </div>
    );
};

export default Alerts;
